import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ChevronLeft, Sparkles, Wand2, Upload, FileText, Camera, Users, DollarSign,
  ClipboardCheck, ScrollText, Trophy, Lock, CheckCircle2, Clock, ShieldCheck,
} from "lucide-react";
import AISaintWordmark from "@/components/AISaintWordmark";

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      base64: reader.result.split(",")[1],
      dataUrl: reader.result,
      filename: file.name,
      size: file.size,
    });
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function WorkOrderDetail() {
  const { wid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [wo, setWo] = useState(null);
  const [plan, setPlan] = useState("standard");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/work-orders/${wid}`);
      setWo(data);
    } catch (err) {
      console.error("wo detail failed:", err);
      toast.error(err.response?.data?.detail || "Could not load work order");
    }
  }, [wid]);

  const loadSub = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription");
      setPlan(data.plan_id || "standard");
    } catch (err) {
      console.error("sub load failed:", err);
    }
  }, []);

  useEffect(() => { load(); loadSub(); }, [load, loadSub]);

  const withBusy = async (label, fn) => {
    setBusy(label);
    try { await fn(); }
    catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };

  const toggleChecklist = (item_id, done) =>
    withBusy(`chk-${item_id}`, async () => {
      await api.post(`/work-orders/${wid}/checklist`, { item_id, done });
      await load();
    });

  const generateScope = () => withBusy("scope", async () => {
    await api.post(`/work-orders/${wid}/ai-scope`, {});
    toast.success("AI Saint scope drafted");
    await load();
  });

  const analyzeBids = () => withBusy("analyze", async () => {
    await api.post(`/work-orders/${wid}/analyze-bids`);
    toast.success("Bids analyzed");
    await load();
  });

  const generateContract = () => withBusy("contract", async () => {
    await api.post(`/work-orders/${wid}/generate-contract`);
    toast.success("Contract drafted");
    await load();
  });

  const selectBid = (bid_id) => withBusy(`sel-${bid_id}`, async () => {
    await api.post(`/work-orders/${wid}/bids/select`, { bid_id });
    await load();
  });

  if (!wo) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  const canAnalyze = ["pro", "elite"].includes(plan);
  const canContract = plan === "elite";

  return (
    <div className="space-y-6" data-testid="wo-detail-page">
      <button className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" onClick={() => nav("/work-orders")} data-testid="wo-back-btn">
        <ChevronLeft className="w-4 h-4" /> Back to work orders
      </button>

      {/* Header + completion meter */}
      <div className="hoa-card royal-ring p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 text-xs mb-1">
              <span className="bg-primary/15 text-gold px-2 py-0.5 rounded-full font-medium">{wo.status.replace(/_/g, " ")}</span>
              <span className="bg-secondary/15 text-secondary px-2 py-0.5 rounded-full font-medium">{wo.priority}</span>
              <span className="text-muted-foreground">{wo.category} · {wo.owner_name}{wo.owner_unit ? ` (${wo.owner_unit})` : ""}</span>
              {wo.is_sample && <span className="bg-gold/20 text-gold px-2 py-0.5 rounded-full font-medium">SAMPLE</span>}
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">{wo.title}</h1>
            <p className="text-foreground/70 mt-2 leading-relaxed">{wo.description}</p>
            <div className="text-xs text-muted-foreground mt-3">Requested by {wo.owner_name} · {new Date(wo.created_at).toLocaleString()}</div>
          </div>
          <div className="min-w-[220px]">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Completion</div>
            <div className="h-3 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${wo.completion_pct || 0}%` }} />
            </div>
            <div className="flex items-center justify-between text-xs mt-1">
              <span className="text-muted-foreground">{wo.start_date ? new Date(wo.start_date).toLocaleDateString() : "—"}</span>
              <span className="font-mono text-gold font-semibold">{wo.completion_pct || 0}%</span>
              <span className="text-muted-foreground">{wo.end_date ? new Date(wo.end_date).toLocaleDateString() : "—"}</span>
            </div>
            {wo.assignment?.committee_name && (
              <div className="text-xs text-muted-foreground mt-3">
                <div className="flex items-center gap-1"><Users className="w-3 h-3" /> {wo.assignment.committee_name}</div>
                {wo.assignment.director_name && <div>Lead: {wo.assignment.director_name}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabbed workflow */}
      <Tabs defaultValue="workflow">
        <TabsList className="flex-wrap h-auto" data-testid="wo-tabs">
          <TabsTrigger value="workflow" data-testid="tab-workflow"><ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Workflow</TabsTrigger>
          <TabsTrigger value="details" data-testid="tab-details"><FileText className="w-3.5 h-3.5 mr-1" /> Details & photos</TabsTrigger>
          <TabsTrigger value="scope" data-testid="tab-scope"><Sparkles className="w-3.5 h-3.5 mr-1" /> AI scope</TabsTrigger>
          <TabsTrigger value="bids" data-testid="tab-bids"><ScrollText className="w-3.5 h-3.5 mr-1" /> Bids ({wo.bids?.length || 0})</TabsTrigger>
          <TabsTrigger value="contract" data-testid="tab-contract"><ShieldCheck className="w-3.5 h-3.5 mr-1" /> Contract</TabsTrigger>
          <TabsTrigger value="payapps" data-testid="tab-payapps"><DollarSign className="w-3.5 h-3.5 mr-1" /> Pay apps ({wo.pay_apps?.length || 0})</TabsTrigger>
          <TabsTrigger value="closeout" data-testid="tab-closeout"><Trophy className="w-3.5 h-3.5 mr-1" /> Closeout</TabsTrigger>
        </TabsList>

        {/* WORKFLOW */}
        <TabsContent value="workflow" className="mt-4">
          <div className="hoa-card p-6">
            <h2 className="text-xl font-semibold mb-4">Workflow checklist</h2>
            <div className="space-y-2">
              {(wo.checklist || []).map((c) => (
                <div key={c.id} className={`flex items-start gap-3 p-3 rounded-lg border ${c.done ? "bg-primary/5 border-primary/30" : "bg-accent/20 border-border"}`} data-testid={`chk-${c.id}`}>
                  <Checkbox checked={c.done} onCheckedChange={(v) => toggleChecklist(c.id, !!v)} disabled={!isBoard || busy === `chk-${c.id}`} className="mt-0.5" />
                  <div className="flex-1">
                    <div className={`font-medium ${c.done ? "text-gold" : "text-foreground"}`}>{c.label}</div>
                    <div className="text-xs text-muted-foreground">
                      Phase: {c.phase} {c.done && c.done_at ? `· ${new Date(c.done_at).toLocaleString()} by ${c.done_by}` : ""}
                    </div>
                  </div>
                  {c.done && <CheckCircle2 className="w-5 h-5 text-gold" />}
                </div>
              ))}
            </div>
          </div>
          <AssignPanel wo={wo} reload={load} isBoard={isBoard} />
        </TabsContent>

        {/* DETAILS + PHOTOS */}
        <TabsContent value="details" className="mt-4 space-y-4">
          <PhotosPanel wo={wo} reload={load} isBoard={isBoard} />
        </TabsContent>

        {/* AI SCOPE */}
        <TabsContent value="scope" className="mt-4">
          <div className="hoa-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold"><AISaintWordmark /> scope of work</h2>
              {isBoard && (
                <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" onClick={generateScope} disabled={busy === "scope"} data-testid="generate-scope-btn">
                  <Wand2 className="w-4 h-4 mr-1.5" /> {wo.ai_scope ? "Regenerate" : "Generate scope"}
                </Button>
              )}
            </div>
            {!wo.ai_scope ? (
              <p className="text-sm text-muted-foreground">No scope yet. Click <span className="text-gold font-medium">Generate scope</span> to have AI Saint draft the SoW, insurance requirements, indemnity, contractor qualifier, and board vote language.</p>
            ) : (
              <ScopeDisplay scope={wo.ai_scope} />
            )}
          </div>
        </TabsContent>

        {/* BIDS */}
        <TabsContent value="bids" className="mt-4 space-y-4">
          <BidsPanel wo={wo} reload={load} isBoard={isBoard} plan={plan} onAnalyze={analyzeBids} onSelect={selectBid} busy={busy} />
        </TabsContent>

        {/* CONTRACT */}
        <TabsContent value="contract" className="mt-4">
          <ContractPanel wo={wo} isBoard={isBoard} canContract={canContract} plan={plan} onGenerate={generateContract} busy={busy} />
        </TabsContent>

        {/* PAY APPS */}
        <TabsContent value="payapps" className="mt-4 space-y-4">
          <PayAppsPanel wo={wo} reload={load} isBoard={isBoard} />
        </TabsContent>

        {/* CLOSEOUT */}
        <TabsContent value="closeout" className="mt-4">
          <ClosePanel wo={wo} reload={load} isBoard={isBoard} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ScopeDisplay({ scope }) {
  return (
    <div className="space-y-4 text-sm">
      <Section title="Scope of Work">
        <ul className="space-y-1">{(scope.scope_of_work || []).map((s, i) => <li key={`sow-${i}-${String(s).slice(0,20)}`}>• {s}</li>)}</ul>
      </Section>
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Insurance Requirements">
          <ul className="space-y-1">{(scope.insurance_requirements || []).map((s, i) => <li key={`ins-${i}-${String(s).slice(0,20)}`}>• {s}</li>)}</ul>
        </Section>
        <Section title="Contractor Qualifier">
          <ul className="space-y-1">{(scope.contractor_qualifier || []).map((s, i) => <li key={`cq-${i}-${String(s).slice(0,20)}`}>• {s}</li>)}</ul>
        </Section>
      </div>
      <Section title="Indemnification"><p className="text-foreground/85">{scope.indemnification_clause}</p></Section>
      <Section title="Bid Instructions"><p className="text-foreground/85">{scope.bid_instructions}</p></Section>
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Estimated Cost Range"><p className="text-gold font-mono font-semibold">{scope.estimated_cost_range}</p></Section>
        <Section title="Min Bids"><p className="text-gold font-mono font-semibold">{scope.recommended_min_bids || 3}</p></Section>
      </div>
      <Section title="Board Vote Language"><p className="text-foreground/85 italic">{scope.board_vote_language}</p></Section>
      {scope.generated_at && <div className="text-xs text-muted-foreground">Drafted {new Date(scope.generated_at).toLocaleString()} by {scope.generated_by}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="p-4 rounded-lg bg-accent/20 border border-border">
      <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

function PhotosPanel({ wo, reload, isBoard }) {
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [fileData, setFileData] = useState(null);

  const upload = async (e) => {
    e.preventDefault();
    if (!fileData) return toast.error("Pick a photo first");
    setUploading(true);
    try {
      await api.post(`/work-orders/${wo.id}/photos`, { photo_base64: fileData.base64, filename: fileData.filename, caption });
      toast.success("Photo uploaded");
      setCaption(""); setFileData(null);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="hoa-card p-6">
      <h2 className="text-xl font-semibold mb-4">Photos & attachments</h2>
      {isBoard && (
        <form onSubmit={upload} className="flex flex-wrap items-end gap-3 mb-5 pb-5 border-b border-border" data-testid="photo-upload-form">
          <div className="flex-1 min-w-[200px]">
            <Label>Photo file</Label>
            <Input type="file" accept="image/*" onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (f.size > 3_000_000) { toast.error("Max 3MB"); return; }
              setFileData(await readFileAsBase64(f));
            }} className="mt-1.5 h-11" data-testid="photo-file-input" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label>Caption</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Damage after Nov 12 storm" className="mt-1.5 h-11" data-testid="photo-caption-input" />
          </div>
          <Button type="submit" disabled={uploading || !fileData} className="rounded-full" data-testid="photo-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : "Upload"}
          </Button>
        </form>
      )}
      {(wo.photos || []).length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-8">
          <Camera className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No photos yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {wo.photos.map((p) => (
            <div key={p.id} className="rounded-lg overflow-hidden border border-border" data-testid={`photo-${p.id}`}>
              <img src={`data:image/jpeg;base64,${p.photo_base64}`} alt={p.caption || p.filename} className="w-full aspect-square object-cover" />
              <div className="p-2 text-xs">
                <div className="font-medium truncate">{p.caption || p.filename}</div>
                <div className="text-muted-foreground">{p.uploaded_by}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BidsPanel({ wo, reload, isBoard, plan, onAnalyze, onSelect, busy }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ contractor_name: "", contractor_email: "", contractor_phone: "", license_number: "", insurance_carrier: "", insurance_expires: "", amount: 0, timeline_days: 14, scope_summary: "" });
  const [fileData, setFileData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, amount: Number(form.amount), timeline_days: Number(form.timeline_days) };
      if (fileData) { payload.file_base64 = fileData.base64; payload.file_name = fileData.filename; }
      await api.post(`/work-orders/${wo.id}/bids`, payload);
      toast.success("Bid added");
      setForm({ contractor_name: "", contractor_email: "", contractor_phone: "", license_number: "", insurance_carrier: "", insurance_expires: "", amount: 0, timeline_days: 14, scope_summary: "" });
      setFileData(null); setOpen(false);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add bid");
    } finally { setSubmitting(false); }
  };

  const canAnalyze = ["pro", "elite"].includes(plan);
  const bids = wo.bids || [];
  const analysis = wo.bid_analysis;
  const rankMap = new Map((analysis?.ranked || []).map((r) => [r.bid_id, r]));

  return (
    <>
      <div className="hoa-card p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-xl font-semibold">Bids ({bids.length}/3+)</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Minimum 3 bids from qualified contractors — recommended.</p>
          </div>
          <div className="flex gap-2">
            {isBoard && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full" data-testid="add-bid-btn"><Upload className="w-4 h-4 mr-1.5" /> Add bid</Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader><DialogTitle>Add contractor bid</DialogTitle></DialogHeader>
                  <form onSubmit={submit} className="space-y-3" data-testid="bid-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Contractor name</Label><Input required value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} className="mt-1.5 h-11" data-testid="bid-name-input" /></div>
                      <div><Label>License #</Label><Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="mt-1.5 h-11" data-testid="bid-license-input" /></div>
                      <div><Label>Email</Label><Input type="email" value={form.contractor_email} onChange={(e) => setForm({ ...form, contractor_email: e.target.value })} className="mt-1.5 h-11" /></div>
                      <div><Label>Phone</Label><Input value={form.contractor_phone} onChange={(e) => setForm({ ...form, contractor_phone: e.target.value })} className="mt-1.5 h-11" /></div>
                      <div><Label>Insurance carrier</Label><Input value={form.insurance_carrier} onChange={(e) => setForm({ ...form, insurance_carrier: e.target.value })} className="mt-1.5 h-11" /></div>
                      <div><Label>Insurance expires</Label><Input type="date" value={form.insurance_expires} onChange={(e) => setForm({ ...form, insurance_expires: e.target.value })} className="mt-1.5 h-11" /></div>
                      <div><Label>Amount ($)</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11" data-testid="bid-amount-input" /></div>
                      <div><Label>Timeline (days)</Label><Input required type="number" value={form.timeline_days} onChange={(e) => setForm({ ...form, timeline_days: e.target.value })} className="mt-1.5 h-11" data-testid="bid-timeline-input" /></div>
                    </div>
                    <div><Label>Scope summary</Label><Textarea required value={form.scope_summary} onChange={(e) => setForm({ ...form, scope_summary: e.target.value })} className="mt-1.5 min-h-[80px]" data-testid="bid-scope-input" /></div>
                    <div><Label>Bid document (optional PDF)</Label><Input type="file" accept=".pdf,image/*" onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 6_000_000) { toast.error("Max 6MB"); return; }
                      setFileData(await readFileAsBase64(f));
                    }} className="mt-1.5 h-11" /></div>
                    <DialogFooter>
                      <Button type="submit" disabled={submitting} className="rounded-full" data-testid="bid-submit-btn">
                        {submitting ? "Saving…" : "Save bid"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            {isBoard && (
              canAnalyze ? (
                <Button variant="outline" className="rounded-full" onClick={onAnalyze} disabled={bids.length < 2 || busy === "analyze"} data-testid="analyze-bids-btn">
                  <Sparkles className="w-4 h-4 mr-1.5" /> AI compare bids
                </Button>
              ) : (
                <Link to="/subscription">
                  <Button variant="outline" className="rounded-full" data-testid="upgrade-bids-btn">
                    <Lock className="w-4 h-4 mr-1.5" /> Upgrade to Pro for AI comparison
                  </Button>
                </Link>
              )
            )}
          </div>
        </div>

        {analysis && (
          <div className="p-4 rounded-lg bg-primary/10 border border-primary/30 mb-4 text-sm" data-testid="bid-analysis-summary">
            <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-gold" /><span className="font-semibold text-gold"><AISaintWordmark /> recommendation</span></div>
            <p className="text-foreground/85">{analysis.summary}</p>
          </div>
        )}

        {bids.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8">No bids yet.</div>
        ) : (
          <div className="space-y-3">
            {bids.map((b) => {
              const rank = rankMap.get(b.id);
              const isBest = analysis?.best_value_bid_id === b.id;
              const isTA = analysis?.technically_acceptable_bid_ids?.includes(b.id);
              const isSelected = wo.selected_bid_id === b.id;
              return (
                <div key={b.id} className={`hoa-card p-4 border ${isSelected ? "border-gold" : "border-border"}`} data-testid={`bid-${b.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-foreground">{b.contractor_name}</span>
                        {isBest && <span className="text-xs bg-gold/25 text-gold px-2 py-0.5 rounded-full font-semibold">BEST VALUE</span>}
                        {isTA && !isBest && <span className="text-xs bg-primary/20 text-gold px-2 py-0.5 rounded-full font-semibold">Technically acceptable</span>}
                        {isSelected && <span className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full font-semibold">SELECTED</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {b.license_number ? `License ${b.license_number} · ` : ""}{b.insurance_carrier ? `${b.insurance_carrier} exp ${b.insurance_expires || "—"} · ` : ""}{b.contractor_email}
                      </div>
                      <p className="text-sm mt-2 text-foreground/85">{b.scope_summary}</p>
                      {rank && (
                        <div className="mt-2 text-xs">
                          <div><span className="font-semibold text-gold">Score: {rank.score}/100</span> · {rank.recommendation}</div>
                          {rank.strengths?.length > 0 && <div className="text-primary/80 mt-1">✓ {rank.strengths.join(" · ")}</div>}
                          {rank.concerns?.length > 0 && <div className="text-destructive/90 mt-0.5">⚠ {rank.concerns.join(" · ")}</div>}
                        </div>
                      )}
                    </div>
                    <div className="text-right min-w-[140px]">
                      <div className="text-2xl font-mono text-gold font-semibold">${Number(b.amount).toFixed(0)}</div>
                      <div className="text-xs text-muted-foreground">{b.timeline_days} days</div>
                      {isBoard && !isSelected && (
                        <Button size="sm" className="rounded-full mt-2" onClick={() => onSelect(b.id)} disabled={busy === `sel-${b.id}`} data-testid={`select-bid-${b.id}`}>
                          Select as winner
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function ContractPanel({ wo, isBoard, canContract, plan, onGenerate, busy }) {
  const ag = wo.contractor_agreement;
  if (!canContract) {
    return (
      <div className="hoa-card royal-ring p-8 text-center" data-testid="contract-locked">
        <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
        <h2 className="text-2xl font-semibold mb-2">Auto-generated contracts are an Elite feature</h2>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto mb-4">Elite plans include self-performing management with AI-assist AI Saint contractor agreements, insurance/COI tracking, and pay-app workflow — the full owner's-rep experience.</p>
        <Link to="/subscription"><Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="upgrade-contract-btn">Upgrade to Elite</Button></Link>
      </div>
    );
  }
  return (
    <div className="hoa-card p-6" data-testid="contract-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Contractor agreement</h2>
        {isBoard && (
          <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" onClick={onGenerate} disabled={!wo.selected_bid_id || busy === "contract"} data-testid="generate-contract-btn">
            <Wand2 className="w-4 h-4 mr-1.5" /> {ag ? "Regenerate" : "Generate contract"}
          </Button>
        )}
      </div>
      {!wo.selected_bid_id && <p className="text-sm text-muted-foreground">Select a winning bid first.</p>}
      {ag && (
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-accent/20 border border-border">
            <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">Agreement text</div>
            <pre className="whitespace-pre-wrap font-serif text-sm text-foreground/85 leading-relaxed">{ag.contract_text}</pre>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-accent/20 border border-border">
              <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">Attachments checklist</div>
              <div className="space-y-1.5">
                {(ag.attachments_required || []).map((a) => (
                  <div key={a.id} className="flex items-center gap-2 text-sm">
                    {a.uploaded ? <CheckCircle2 className="w-4 h-4 text-gold" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
                    <span className={a.uploaded ? "text-gold" : "text-foreground/85"}>{a.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 rounded-lg bg-accent/20 border border-border">
              <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">Payment schedule</div>
              <div className="space-y-1.5 text-sm">
                {(ag.payment_schedule || []).map((p, i) => (
                  <div key={`ps-${i}-${p.milestone}`} className="flex items-center justify-between">
                    <span className="text-foreground/85">{p.milestone}</span>
                    <span className="font-mono text-gold">{p.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PayAppsPanel({ wo, reload, isBoard }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: 0, pct_complete: 0, description: "" });
  const [fileData, setFileData] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount), pct_complete: Number(form.pct_complete) };
      if (fileData) { payload.invoice_base64 = fileData.base64; payload.invoice_filename = fileData.filename; }
      await api.post(`/work-orders/${wo.id}/pay-app`, payload);
      toast.success("Pay app recorded");
      setForm({ amount: 0, pct_complete: 0, description: "" }); setFileData(null); setOpen(false);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record");
    } finally { setSaving(false); }
  };

  return (
    <div className="hoa-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Pay applications</h2>
        {isBoard && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-payapp-btn"><Upload className="w-4 h-4 mr-1.5" /> Record pay app</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Record pay application</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="payapp-form">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Amount ($)</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5 h-11" data-testid="payapp-amount-input" /></div>
                  <div><Label>% Complete</Label><Input required type="number" min="0" max="100" value={form.pct_complete} onChange={(e) => setForm({ ...form, pct_complete: e.target.value })} className="mt-1.5 h-11" data-testid="payapp-pct-input" /></div>
                </div>
                <div><Label>Description</Label><Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 min-h-[70px]" data-testid="payapp-desc-input" /></div>
                <div><Label>Invoice (optional)</Label><Input type="file" accept=".pdf,image/*" onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 6_000_000) { toast.error("Max 6MB"); return; }
                  setFileData(await readFileAsBase64(f));
                }} className="mt-1.5 h-11" /></div>
                <DialogFooter><Button type="submit" disabled={saving} className="rounded-full" data-testid="payapp-submit-btn">{saving ? "Saving…" : "Record"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {(wo.pay_apps || []).length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No pay applications recorded yet.</p>
      ) : (
        <div className="space-y-2">
          {wo.pay_apps.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-3 p-3 rounded-lg bg-accent/20 border border-border items-center" data-testid={`payapp-${p.id}`}>
              <div className="col-span-2 text-xs text-muted-foreground">{new Date(p.recorded_at).toLocaleDateString()}</div>
              <div className="col-span-5 text-sm">{p.description}</div>
              <div className="col-span-2 text-xs">{p.pct_complete}% complete</div>
              <div className="col-span-3 text-right font-mono text-gold font-semibold">${Number(p.amount).toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClosePanel({ wo, reload, isBoard }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ passed: true, notes: "", warranty_expires: "" });
  const [fileData, setFileData] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (fileData) { payload.warranty_base64 = fileData.base64; payload.warranty_filename = fileData.filename; }
      await api.post(`/work-orders/${wo.id}/walkthrough`, payload);
      toast.success("Walkthrough saved");
      setForm({ passed: true, notes: "", warranty_expires: "" }); setFileData(null); setOpen(false);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="hoa-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Walkthrough & warranties</h2>
        {isBoard && !wo.walkthrough && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="walkthrough-btn"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Record walkthrough</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Punch-list walkthrough & closeout</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="walkthrough-form">
                <div><Label>Result</Label>
                  <Select value={form.passed ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, passed: v === "yes" })}>
                    <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Passed — accept work</SelectItem>
                      <SelectItem value="no">Failed — punch list open</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Notes</Label><Textarea required value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 min-h-[90px]" data-testid="walkthrough-notes-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Warranty file</Label><Input type="file" accept=".pdf,image/*" onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 6_000_000) { toast.error("Max 6MB"); return; }
                    setFileData(await readFileAsBase64(f));
                  }} className="mt-1.5 h-11" /></div>
                  <div><Label>Warranty expires</Label><Input type="date" value={form.warranty_expires} onChange={(e) => setForm({ ...form, warranty_expires: e.target.value })} className="mt-1.5 h-11" /></div>
                </div>
                <DialogFooter><Button type="submit" disabled={saving} className="rounded-full" data-testid="walkthrough-submit-btn">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
      {wo.walkthrough ? (
        <div className={`p-4 rounded-lg border ${wo.walkthrough.passed ? "bg-primary/10 border-primary/40" : "bg-destructive/10 border-destructive/40"}`} data-testid="walkthrough-result">
          <div className="flex items-center gap-2 mb-2">
            {wo.walkthrough.passed ? <Trophy className="w-5 h-5 text-gold" /> : <ClipboardCheck className="w-5 h-5 text-destructive" />}
            <span className="font-semibold">{wo.walkthrough.passed ? "Work accepted" : "Punch list open"}</span>
            <span className="text-xs text-muted-foreground">· {new Date(wo.walkthrough.at).toLocaleString()} · {wo.walkthrough.by}</span>
          </div>
          <p className="text-sm text-foreground/85 whitespace-pre-wrap">{wo.walkthrough.notes}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No walkthrough recorded yet.</p>
      )}
      {(wo.warranties || []).length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-2">Warranties on file</div>
          <div className="space-y-2">
            {wo.warranties.map((w) => (
              <div key={w.id} className="flex items-center justify-between p-3 rounded-lg bg-accent/20 border border-border text-sm" data-testid={`warranty-${w.id}`}>
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> {w.filename}</div>
                <div className="text-xs text-muted-foreground">{w.expires ? `Expires ${w.expires}` : "No expiry set"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AssignPanel({ wo, reload, isBoard }) {
  const [form, setForm] = useState({
    committee_name: wo.assignment?.committee_name || "",
    director_name: wo.assignment?.director_name || "",
    start_date: wo.start_date ? wo.start_date.slice(0, 10) : "",
    end_date: wo.end_date ? wo.end_date.slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/work-orders/${wo.id}/assign`, form);
      toast.success("Assignment saved");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  if (!isBoard) return null;
  return (
    <div className="hoa-card p-6 mt-4">
      <h2 className="text-xl font-semibold mb-4">Assignment & schedule</h2>
      <form onSubmit={submit} className="grid grid-cols-2 gap-3" data-testid="assign-form">
        <div><Label>Committee</Label><Input value={form.committee_name} onChange={(e) => setForm({ ...form, committee_name: e.target.value })} placeholder="e.g. Buildings & Grounds" className="mt-1.5 h-11" data-testid="assign-committee-input" /></div>
        <div><Label>Director in charge</Label><Input value={form.director_name} onChange={(e) => setForm({ ...form, director_name: e.target.value })} placeholder="e.g. Marcus Johnson" className="mt-1.5 h-11" data-testid="assign-director-input" /></div>
        <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 h-11" data-testid="assign-start-input" /></div>
        <div><Label>Target end date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 h-11" data-testid="assign-end-input" /></div>
        <div className="col-span-2"><Button type="submit" disabled={saving} className="rounded-full" data-testid="assign-submit-btn">{saving ? "Saving…" : "Save assignment"}</Button></div>
      </form>
    </div>
  );
}
